import { cn } from '@/lib/utils'

/**
 * Skeleton 组件属性
 */
export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 变体类型 */
  variant?: 'text' | 'circular' | 'rectangular' | 'card' | 'table-row'
  /** 宽度 */
  width?: string | number
  /** 高度 */
  height?: string | number
  /** 重复数量 */
  count?: number
}

/**
 * Skeleton 骨架屏组件
 * 用于在数据加载时显示占位内容
 */
export function Skeleton({
  className,
  variant = 'rectangular',
  width,
  height,
  count = 1,
  ...props
}: SkeletonProps) {
  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  }

  const variantClasses = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-md',
    card: 'rounded-lg',
    'table-row': 'h-12 rounded',
  }

  const skeletons = Array.from({ length: count }, (_, i) => (
    <div
      key={i}
      className={cn(
        'animate-pulse bg-muted',
        variantClasses[variant],
        className
      )}
      style={style}
      {...props}
    />
  ))

  return count === 1 ? skeletons[0] : <>{skeletons}</>
}

// ==================== 预设骨架屏 ====================

/**
 * 列表页骨架屏
 */
export function ListPageSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-4">
      {/* 标题和操作栏 */}
      <div className="flex items-center justify-between">
        <Skeleton width={200} height={32} />
        <Skeleton width={120} height={36} />
      </div>

      {/* 筛选栏 */}
      <div className="flex gap-4">
        <Skeleton width={200} height={40} />
        <Skeleton width={150} height={40} />
        <Skeleton width={150} height={40} />
      </div>

      {/* 列表项 */}
      <div className="space-y-3">
        {Array.from({ length: rows }, (_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 rounded-lg border p-4"
          >
            <Skeleton variant="circular" width={40} height={40} />
            <div className="flex-1 space-y-2">
              <Skeleton width="60%" height={20} />
              <Skeleton width="40%" height={16} />
            </div>
            <Skeleton width={80} height={24} />
          </div>
        ))}
      </div>

      {/* 分页 */}
      <div className="flex justify-center gap-2">
        <Skeleton width={80} height={32} />
        <Skeleton width={32} height={32} />
        <Skeleton width={32} height={32} />
        <Skeleton width={32} height={32} />
        <Skeleton width={80} height={32} />
      </div>
    </div>
  )
}

/**
 * 详情页骨架屏
 */
export function DetailPageSkeleton() {
  return (
    <div className="space-y-6">
      {/* 标题 */}
      <div className="space-y-2">
        <Skeleton width={300} height={32} />
        <Skeleton width={200} height={20} />
      </div>

      {/* 信息卡片 */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="rounded-lg border p-4 space-y-2">
            <Skeleton width={80} height={16} />
            <Skeleton width={120} height={24} />
          </div>
        ))}
      </div>

      {/* 内容区域 */}
      <div className="rounded-lg border p-6 space-y-4">
        <Skeleton width={150} height={24} />
        <div className="space-y-2">
          <Skeleton width="100%" height={16} />
          <Skeleton width="90%" height={16} />
          <Skeleton width="95%" height={16} />
          <Skeleton width="80%" height={16} />
        </div>
      </div>
    </div>
  )
}

/**
 * 卡片网格骨架屏
 */
export function CardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton variant="circular" width={48} height={48} />
            <div className="flex-1 space-y-2">
              <Skeleton width="70%" height={20} />
              <Skeleton width="50%" height={16} />
            </div>
          </div>
          <Skeleton width="100%" height={60} />
          <div className="flex justify-between">
            <Skeleton width={80} height={24} />
            <Skeleton width={60} height={24} />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * 表格行骨架屏
 */
export function TableRowsSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-2">
      {/* 表头 */}
      <div className="flex gap-4 border-b pb-2">
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} width={`${100 / columns}%`} height={20} />
        ))}
      </div>

      {/* 表格行 */}
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex gap-4 py-3 border-b">
          {Array.from({ length: columns }, (_, j) => (
            <Skeleton key={j} width={`${100 / columns}%`} height={16} />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * 表单骨架屏
 */
export function FormSkeleton({ fields = 4 }: { fields?: number }) {
  return (
    <div className="space-y-6">
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton width={100} height={16} />
          <Skeleton width="100%" height={40} />
        </div>
      ))}
      <div className="flex justify-end gap-3">
        <Skeleton width={80} height={40} />
        <Skeleton width={100} height={40} />
      </div>
    </div>
  )
}

/**
 * 统计卡片骨架屏
 */
export function StatCardsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="rounded-lg border p-4 space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton width={80} height={16} />
            <Skeleton variant="circular" width={24} height={24} />
          </div>
          <Skeleton width={120} height={32} />
          <Skeleton width={100} height={14} />
        </div>
      ))}
    </div>
  )
}

/**
 * 侧边栏骨架屏
 */
export function SidebarSkeleton() {
  return (
    <div className="space-y-4 p-4">
      {/* Logo */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton width={100} height={20} />
      </div>

      {/* 导航项 */}
      <div className="space-y-2">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="flex items-center gap-3 p-2">
            <Skeleton width={20} height={20} />
            <Skeleton width={80} height={16} />
          </div>
        ))}
      </div>
    </div>
  )
}
